import {
  BOSS_REACTION_ROUND_DIFFICULTY,
  COMMON_NODE_POOL,
  ENCOUNTER_PHYSICAL_COST,
  ENCOUNTER_WIN_CHANCE_CEILING,
  ENCOUNTER_WIN_CHANCE_FLOOR,
  NODES_PER_SEASON,
  OPPONENT_PROFILES,
  REWARDS,
  REWARD_IDS,
  RENEWAL_OVR_REPUTATION_THRESHOLD,
  ROUTE_NODE_COUNTS,
  WORLD_CUP_ROUTE_NODE_COUNTS,
  WORLD_CUP_OVR_THRESHOLD,
  WORLD_CUP_REPUTATION_THRESHOLD,
  getStageBossBalance,
} from "./constants";
import { getClubCompetitiveProfile, getClubTier, getStartingClubs, type LeaguePrestige } from "./clubs";
import { RELIC_SYNERGIES } from "./synergies";
import { isWorldCup2026Qualified } from "./worldCup2026";
import type {
  CardRewardId,
  CareerMoveKind,
  EncounterKind,
  FinaleKind,
  GameStateV2,
  Inventory,
  MapNode,
  MatchOutcome,
  NodeType,
  OpponentProfileId,
  PendingMatch,
  Player,
  RelicRewardId,
  RewardId,
  RewardEffect,
} from "./types";

export const clamp = (value: number, min = 0, max = 100): number =>
  Math.max(min, Math.min(max, value));

export function appearanceProbability(reputation: number): number {
  return clamp(.35 + clamp(reputation) * .0065, .35, 1);
}

export interface CareerMoveOutcome {
  clubReputation: number;
  morale: number;
  ovr: number;
  oneClubEligible: boolean;
  appearanceProbability: number;
  currentCompetitiveLevel: number;
  destinationCompetitiveLevel: number;
  destinationLeaguePrestige: LeaguePrestige;
}

export interface RankedTransferDecision {
  club: string;
  risk: "medium" | "high";
}

export type CareerDecisionRisk = "low" | "medium" | "high";

type CareerMoveState = Pick<GameStateV2, "club" | "clubReputation" | "morale" | "ovr" | "oneClubEligible">
  & Partial<Pick<GameStateV2, "leagueMovements">>;

export function getCareerMoveRisk(moveKind: "renewal" | "reserve-promotion"): "low";
export function getCareerMoveRisk(moveKind: "transfer", transferIndex?: number, transferCount?: number): "medium" | "high";
export function getCareerMoveRisk(
  moveKind: CareerMoveKind,
  transferIndex = 0,
  transferCount = 1,
): CareerDecisionRisk {
  if (moveKind !== "transfer") return "low";
  return transferCount > 1 && transferIndex < transferCount - 1 ? "medium" : "high";
}

export function getCareerMoveOutcome(
  state: CareerMoveState,
  club: string,
  moveKind: CareerMoveKind = club === state.club ? "renewal" : "transfer",
): CareerMoveOutcome {
  const movements = state.leagueMovements ?? [];
  const currentProfile = getClubCompetitiveProfile(state.club, movements);
  const destinationProfile = getClubCompetitiveProfile(club, movements);
  const competitiveDrop = moveKind === "transfer"
    ? Math.max(0, currentProfile.level - destinationProfile.level)
    : 0;
  const clubReputation = moveKind === "renewal"
    ? clamp(state.clubReputation + 8)
    : moveKind === "reserve-promotion" ? 35 : clamp(30 + competitiveDrop * 10);
  const morale = moveKind === "renewal"
    ? 78
    : moveKind === "reserve-promotion" ? 75 : clamp(60 + competitiveDrop * 5);
  const continuityGrowth = moveKind === "renewal" && state.clubReputation >= RENEWAL_OVR_REPUTATION_THRESHOLD;
  const sportingTierGrowth = moveKind === "transfer"
    ? Math.min(3, 1 + Math.max(0, destinationProfile.level - currentProfile.level))
    : 0;
  const ovr = clamp(state.ovr + (moveKind === "transfer" ? sportingTierGrowth : continuityGrowth ? 1 : 0), 0, 99);
  return {
    clubReputation,
    morale,
    ovr,
    oneClubEligible: moveKind === "transfer" ? false : state.oneClubEligible,
    appearanceProbability: appearanceProbability(clubReputation),
    currentCompetitiveLevel: currentProfile.level,
    destinationCompetitiveLevel: destinationProfile.level,
    destinationLeaguePrestige: destinationProfile.leaguePrestige,
  };
}

export function getRankedTransferDecisions(
  state: CareerMoveState,
  clubs: readonly string[],
): RankedTransferDecision[] {
  const ordered = clubs
    .map((club, index) => ({ club, index, level: getClubCompetitiveProfile(club, state.leagueMovements ?? []).level }))
    .sort((first, second) => first.level - second.level || first.index - second.index);
  return ordered.map(({ club }, index) => ({
    club,
    risk: getCareerMoveRisk("transfer", index, ordered.length),
  }));
}

export function createInitialState(
  player: Player,
  map: MapNode[][] = generateSeasonMap(),
  club: string = getStartingClubs(player.country)[0]?.name ?? "Athletic Bilbao",
): GameStateV2 {
  return {
    saveVersion: 2,
    player,
    season: 1,
    finaleKind: null,
    week: 0,
    ovr: 60,
    physical: 100,
    morale: 70,
    clubReputation: 45,
    appearances: 0,
    minutesPlayed: 0,
    goals: 0,
    assists: 0,
    titles: 0,
    wins: 0,
    derbyWins: 0,
    bestWinStreak: 0,
    seasonsCompleted: 0,
    careerHistory: [],
    stageBaseline: { appearances: 0, minutesPlayed: 0, goals: 0, assists: 0, ovr: 60, titles: 0 },
    leagueOpponentsPlayed: [],
    leagueMovements: [],
    oneClubEligible: true,
    club,
    inventory: {},
    log: ["Comienza una nueva carrera desde la cantera."],
    map,
    path: [],
    activeNodeId: null,
    boss: null,
    finished: false,
    selectedCardId: null,
    pendingMatch: null,
    pendingScoutOffer: [],
    winStreak: 0,
    relicUses: {},
  };
}

export function shuffle<T>(items: readonly T[], random: () => number = Math.random): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    [result[index], result[other]] = [result[other], result[index]];
  }
  return result;
}

export function generateSeasonMap(
  random: () => number = Math.random,
  idFactory: (column: number, row: number) => string = (column, row) =>
    `s${Date.now()}-${column}-${row}-${Math.floor(random() * 0xffffff).toString(36)}`,
): MapNode[][] {
  const map = ROUTE_NODE_COUNTS.map((nodeCount, column) => {
    let types: NodeType[];
    if (column === 3) {
      const sides = shuffle(COMMON_NODE_POOL, random).slice(0, nodeCount - 1);
      types = [sides[0] ?? "match", "derby", sides[1] ?? "match"];
    } else if (column === NODES_PER_SEASON - 1) {
      const nonRest = shuffle(COMMON_NODE_POOL.filter((type) => type !== "rest"), random)[0] ?? "match";
      types = random() < 0.5 ? ["rest", nonRest] : [nonRest, "rest"];
    } else {
      const basePool = column === 1 || column === 5
        ? COMMON_NODE_POOL.filter((type) => type !== "match")
        : COMMON_NODE_POOL;
      const pool = Array.from({ length: Math.ceil(nodeCount / basePool.length) }, () => basePool).flat();
      types = shuffle(pool, random).slice(0, nodeCount);
    }
    return types.map((type, row) => ({ id: idFactory(column, row), type, row, column }));
  });
  return preventRepeatedSupportNodes(map, true);
}

export function generateWorldCupMap(
  random: () => number = Math.random,
  idFactory: (column: number, row: number) => string = (column, row) =>
    `wc${Date.now()}-${column}-${row}-${Math.floor(random() * 0xffffff).toString(36)}`,
): MapNode[][] {
  const phases: readonly (readonly NodeType[])[] = [
    ["match", "match"],
    shuffle(["rest", "event", "scout"], random),
    ["match", "match", "match", "match"],
    ["match", "match", "match"],
    ["rest", "rest"],
  ];
  return preventRepeatedSupportNodes(
    phases.map((types, column) => types.map((type, row) => ({ id: idFactory(column, row), type, row, column }))),
    true,
  );
}

export function generateStageMap(stage: number, random: () => number = Math.random, finaleKind: FinaleKind = "world-cup"): MapNode[][] {
  return stage >= 5 && finaleKind === "world-cup" ? generateWorldCupMap(random) : generateSeasonMap(random);
}

/** Aplica las reglas de ruta también a partidas guardadas con mapas anteriores. */
export function enforceSeasonMapStructure(map: MapNode[][]): MapNode[][] {
  if (map.length < NODES_PER_SEASON || !map[3]?.[1] || map.at(-1)?.length !== 2) {
    return preventRepeatedSupportNodes(map);
  }
  const normalized: MapNode[][] = map.map((column) => column.map((node) => ({
    ...node,
    type: node.type === "derby" ? "match" : node.type,
  })));
  normalized[3]![1] = { ...normalized[3]![1]!, type: "derby" };

  const finalStep = normalized.at(-1)!;
  if (finalStep[0]?.type !== "rest" && finalStep[1]?.type !== "rest") {
    const checksum = finalStep.reduce((total, node) => total + node.id.length, 0);
    const restRow = checksum % 2 === 0 ? 0 : 1;
    finalStep[restRow] = { ...finalStep[restRow]!, type: "rest" };
  }
  return preventRepeatedSupportNodes(normalized, true);
}

export function enforceStageMapStructure(map: MapNode[][], stage: number, finaleKind: FinaleKind | null = stage >= 5 ? "world-cup" : null): MapNode[][] {
  if (stage < 5 || finaleKind === "treble") return enforceSeasonMapStructure(map);
  const isWorldCupShape = map.length === WORLD_CUP_ROUTE_NODE_COUNTS.length
    && map.every((column, columnIndex) => column.length === WORLD_CUP_ROUTE_NODE_COUNTS[columnIndex]);
  if (!isWorldCupShape) return generateWorldCupMap();
  const normalized: MapNode[][] = map.map((column, columnIndex) => column.map((node): MapNode => {
    if (columnIndex === 0 || columnIndex === 2 || columnIndex === 3) return { ...node, type: "match" };
    if (columnIndex === WORLD_CUP_ROUTE_NODE_COUNTS.length - 1) return { ...node, type: "rest" };
    return node.type === "derby" ? { ...node, type: "event" } : node;
  }));
  return preventRepeatedSupportNodes(normalized, true);
}

function preventRepeatedSupportNodes(map: MapNode[][], preserveFinalRest = false): MapNode[][] {
  const normalized = map.map((column) => column.map((node) => ({ ...node })));
  for (let columnIndex = 1; columnIndex < normalized.length; columnIndex += 1) {
    const previous = normalized[columnIndex - 1] ?? [];
    const current = normalized[columnIndex] ?? [];
    for (const target of current) {
      if (target.type !== "rest" && target.type !== "scout") continue;
      const conflicts = previous.filter((source) => source.type === target.type
        && isRouteTransitionAllowed(source.row, target, normalized.map((column) => column.length)));
      if (!conflicts.length) continue;
      const protectedFinalRest = preserveFinalRest
        && columnIndex === normalized.length - 1
        && target.type === "rest";
      if (protectedFinalRest) {
        for (const source of conflicts) source.type = "event";
      } else {
        target.type = "event";
      }
    }
  }
  return normalized;
}

export function isRouteTransitionAllowed(fromRow: number | null, target: MapNode, routeShape: readonly number[] | number = ROUTE_NODE_COUNTS): boolean {
  const counts = typeof routeShape === "number" ? ROUTE_NODE_COUNTS : routeShape;
  const totalSteps = typeof routeShape === "number" ? routeShape : counts.length;
  if (target.column === 0) return fromRow === null;
  if (fromRow === null) return false;
  if (target.column >= totalSteps) return false;
  const previousCount = counts[target.column - 1] ?? 1;
  const targetCount = counts[target.column] ?? 1;
  const fromPosition = previousCount <= 1 ? .5 : fromRow / (previousCount - 1);
  const targetPosition = targetCount <= 1 ? .5 : target.row / (targetCount - 1);
  return Math.abs(targetPosition - fromPosition) <= .500001;
}

export function canNavigateToNode(
  state: Pick<GameStateV2, "map" | "path" | "week">,
  target: MapNode,
): boolean {
  if (target.column !== state.week || state.week >= state.map.length) return false;
  const routeShape = state.map.map((column) => column.length);
  if (state.week === 0) return isRouteTransitionAllowed(null, target, routeShape);
  const previousId = state.path.at(-1);
  const previous = state.map.flat().find((node) => node.id === previousId);
  return previous ? isRouteTransitionAllowed(previous.row, target, routeShape) : false;
}

export function getInventoryCount(inventory: Inventory, rewardId: RewardId): number {
  return Math.max(0, Math.floor(inventory[rewardId]?.count ?? 0));
}

export function acquireReward(inventory: Inventory, rewardId: RewardId): Inventory {
  const reward = REWARDS[rewardId];
  const count = Math.min(reward.stackLimit ?? Number.POSITIVE_INFINITY, getInventoryCount(inventory, rewardId) + 1);
  return { ...inventory, [rewardId]: { rewardId, kind: reward.kind, count } };
}

export function canAcquireReward(inventory: Inventory, rewardId: RewardId): boolean {
  const limit = REWARDS[rewardId].stackLimit ?? Number.POSITIVE_INFINITY;
  return getInventoryCount(inventory, rewardId) < limit;
}

export function generateScoutOffer(
  inventory: Inventory,
  position: Player["position"],
  random: () => number = Math.random,
): RewardId[] {
  const available = REWARD_IDS.filter((id) => canAcquireReward(inventory, id));
  const ownedRelics = REWARD_IDS
    .filter((id) => REWARDS[id].kind === "relic" && getInventoryCount(inventory, id) > 0)
  const familyCounts = new Map<string, number>();
  for (const id of ownedRelics) {
    const family = REWARDS[id].family;
    familyCounts.set(family, (familyCounts.get(family) ?? 0) + getInventoryCount(inventory, id));
  }
  const alignedFamily = [...familyCounts.entries()].sort((first, second) => second[1] - first[1])[0]?.[0];
  const relics = available.filter((id) => REWARDS[id].kind === "relic");
  const recipePartners = RELIC_SYNERGIES.flatMap((synergy) => {
    const ownsPart = synergy.relics.some((id) => ownedRelics.includes(id));
    return ownsPart ? synergy.relics.filter((id) => relics.includes(id) && !ownedRelics.includes(id)) : [];
  });
  const aligned = shuffle([...new Set(recipePartners)], random)[0]
    ?? shuffle(relics.filter((id) => REWARDS[id].family === alignedFamily), random)[0]
    ?? shuffle(relics, random)[0];
  const wildcard = shuffle(relics.filter((id) => id !== aligned && REWARDS[id].family !== alignedFamily), random)[0]
    ?? shuffle(relics.filter((id) => id !== aligned), random)[0];
  const cards = available.filter((id) => REWARDS[id].kind === "card" && (position === "MCO" || id !== "pase-entre-lineas"));
  const immediate = shuffle(cards, random)[0] ?? shuffle(available.filter((id) => id !== aligned && id !== wildcard), random)[0];
  return [aligned, wildcard, immediate].filter((id): id is RewardId => Boolean(id));
}

export function getRelicEffectAmount(inventory: Inventory, type: RewardEffect["type"]): number {
  return REWARD_IDS.reduce((total, rewardId) => {
    const reward = REWARDS[rewardId];
    if (reward.kind !== "relic" || reward.effect.type !== type) return total;
    const effect = reward.effect;
    if ("amount" in effect) return total + effect.amount * getInventoryCount(inventory, rewardId);
    if ("percentage" in effect) return total + effect.percentage * getInventoryCount(inventory, rewardId);
    return total;
  }, 0);
}

export function getRelicStackBonus(
  inventory: Inventory,
  relicId: RelicRewardId,
): number {
  const reward = REWARDS[relicId];
  const copies = getInventoryCount(inventory, relicId);
  if (reward.effect.type === "physicalCostReduction" || reward.effect.type === "moraleBonus") {
    return reward.effect.amount * copies;
  }
  return 0;
}

export function canUseCard(inventory: Inventory, cardId: CardRewardId, encounter: EncounterKind): boolean {
  const reward = REWARDS[cardId];
  return reward.kind === "card"
    && getInventoryCount(inventory, cardId) > 0
    && reward.allowedEncounters.includes(encounter);
}

export function consumeCard(inventory: Inventory, cardId: CardRewardId): Inventory {
  const current = inventory[cardId];
  if (!current || current.kind !== "card" || current.count < 1) return inventory;
  const next = { ...inventory };
  if (current.count === 1) delete next[cardId];
  else next[cardId] = { ...current, count: current.count - 1 };
  return next;
}

export function effectiveRating(
  state: Pick<GameStateV2, "ovr" | "physical" | "morale"> & Partial<Pick<GameStateV2, "inventory">>,
  extra = 0,
  ignorePhysicalPenalty = false,
): number {
  let physicalPenalty = ignorePhysicalPenalty ? 0 : state.physical >= 70 ? 0 : state.physical >= 50 ? 2 : state.physical >= 30 ? 5 : 9;
  const kneePads = state.inventory?.["rodilleras-antiguas"];
  const kneePadsEffect = REWARDS["rodilleras-antiguas"].effect;
  if (kneePads && kneePadsEffect.type === "physicalPenaltyCap" && state.physical < kneePadsEffect.threshold) {
    physicalPenalty = Math.min(physicalPenalty, kneePadsEffect.amount);
  }
  const moralePenalty = state.morale >= 60 ? 0 : state.morale >= 40 ? 2 : state.morale >= 20 ? 5 : 8;
  const academyPhoto = REWARDS["foto-cantera"].effect;
  const academyPhotoBonus = academyPhoto.type === "lowMoraleRating"
    && getInventoryCount(state.inventory ?? {}, "foto-cantera") > 0
    && moralePenalty > 0
    ? Math.min(academyPhoto.amount, moralePenalty)
    : 0;
  const bracelet = REWARDS.brazalete.effect;
  const braceletCopies = getInventoryCount(state.inventory ?? {}, "brazalete");
  const highMoraleBonus = bracelet.type === "moraleBonus"
    && bracelet.threshold !== undefined
    && state.morale >= bracelet.threshold
    ? Math.min(bracelet.maximumRatingBonus ?? Number.POSITIVE_INFINITY, braceletCopies * (bracelet.ratingAmount ?? 0))
    : 0;
  return state.ovr - physicalPenalty - moralePenalty + academyPhotoBonus + highMoraleBonus + extra;
}

export function getCareerFinale(
  state: Pick<GameStateV2, "club" | "clubReputation" | "ovr" | "leagueMovements" | "player">,
): FinaleKind {
  return state.ovr >= WORLD_CUP_OVR_THRESHOLD
    && state.clubReputation >= WORLD_CUP_REPUTATION_THRESHOLD
    && getClubTier(state.club, state.leagueMovements) === "elite"
    && isWorldCup2026Qualified(state.player.country)
    ? "world-cup"
    : "treble";
}

export function isWorldCupElimination(
  state: Pick<GameStateV2, "season" | "week" | "finaleKind">,
  outcome: MatchOutcome,
  groupWins?: number,
): boolean {
  if (state.season !== 5 || state.finaleKind !== "world-cup") return false;
  if (state.week === 0) return (groupWins ?? 0) < 2;
  return outcome === "loss" && state.week >= 1 && state.week <= 3;
}

export function encounterDifficulty(encounter: EncounterKind, season: number, step = 0): number {
  const balance = getStageBossBalance(season);
  const base = balance.expectedOvr;
  if (encounter === "boss") return balance.bossDifficulty;
  // El derbi mide la progresión de la etapa, no castiga las etapas tardías dos veces.
  // Su dificultad crece con el OVR base esperado, pero parte igualado si el jugador
  // llega con una progresión y una condición normales para ese momento de la carrera.
  if (encounter === "derby") return base;
  const leagueStep = Math.min(Math.max(step, 0), 4);
  return base - 3 + leagueStep * 4;
}

export function getOpponentProfile(
  state: Pick<GameStateV2, "season" | "week" | "activeNodeId" | "boss">,
  encounter: EncounterKind,
) {
  const key = `${state.season}:${state.week}:${state.activeNodeId ?? encounter}:${state.boss?.round ?? 0}`;
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) hash = ((hash << 5) - hash + key.charCodeAt(index)) | 0;
  const ids: readonly OpponentProfileId[] = ["aggressive", "compact", "adaptive"];
  return OPPONENT_PROFILES[ids[Math.abs(hash) % ids.length] ?? "adaptive"];
}

export function calculateWinProbability(effective: number, difficulty: number, randomSpan = 20): number {
  if (randomSpan <= 0) return effective >= difficulty ? 1 : 0;
  const halfSpan = randomSpan / 2;
  return clamp((effective - difficulty + halfSpan) / randomSpan, 0, 1);
}

export function calculateEncounterWinProbability(effective: number, difficulty: number): number {
  return clamp(
    calculateWinProbability(effective, difficulty, 14),
    ENCOUNTER_WIN_CHANCE_FLOOR,
    ENCOUNTER_WIN_CHANCE_CEILING,
  );
}

export type MatchOutlook = "favorable" | "even" | "difficult";
export type BossReactionResource = "card" | "physical" | "morale";

export function matchOutlook(winProbability: number): MatchOutlook {
  if (winProbability >= 0.66) return "favorable";
  if (winProbability >= 0.4) return "even";
  return "difficult";
}

export function bossReactionChance(
  state: GameStateV2,
  resource: BossReactionResource,
  reactionCardId: CardRewardId | null = null,
  encounter: "boss" | "derby" = "boss",
): number {
  const card = resource === "card" && reactionCardId ? REWARDS[reactionCardId] : null;
  const effect = card?.effect;
  const projected = {
    ...state,
    physical: clamp(state.physical + (effect?.type === "physicalRestore" ? effect.amount : 0)),
    morale: clamp(state.morale + (effect?.type === "moraleRestore" ? effect.amount : 0)),
  };
  const profileId = state.pendingMatch?.opponentProfileId ?? getOpponentProfile(state, encounter).id;
  const cardBonus = resource === "card"
    ? cardRatingBonus(reactionCardId, "boss") + cardProfileRatingBonus(reactionCardId, profileId)
    : 0;
  // El recurso elegido importa de forma continua: llegar con 60 de físico siempre
  // ayuda más que llegar con 59, sin añadir otro umbral oculto. Las cartas conservan
  // un pequeño impulso base y suman además su efecto y afinidad propias.
  const reactionBonus = resource === "physical"
    ? 1.5 + projected.physical * 0.035
    : resource === "morale"
      ? 1 + projected.morale * 0.035
      : 3;
  let relicBonus = 0;
  const biometric = REWARDS["pulsera-biometrica"].effect;
  if (biometric.type === "highPhysicalRating" && getInventoryCount(state.inventory, "pulsera-biometrica") > 0 && projected.physical >= biometric.threshold) relicBonus += biometric.amount;
  const clipping = REWARDS["recorte-periodico"].effect;
  if (clipping.type === "streakRating" && getInventoryCount(state.inventory, "recorte-periodico") > 0) relicBonus += Math.min(clipping.maximum, state.winStreak * clipping.amount);
  const rating = effectiveRating(projected, reactionBonus + cardBonus + relicBonus, effect?.type === "ignorePhysicalPenalty");
  const round = encounter === "boss" ? state.boss?.round ?? 0 : 0;
  const difficulty = encounter === "boss"
    ? getStageBossBalance(state.season).bossReactionDifficulty + round * BOSS_REACTION_ROUND_DIFFICULTY
    : encounterDifficulty(encounter, state.season, round);

  // Sigue siendo una apuesta: una build excelente no garantiza el pase y una mala
  // situación nunca convierte la decisión en una derrota automática disfrazada.
  return clamp(
    calculateWinProbability(rating, difficulty, 14),
    ENCOUNTER_WIN_CHANCE_FLOOR,
    ENCOUNTER_WIN_CHANCE_CEILING,
  );
}

export function bossReactionOdds(
  state: GameStateV2,
  resource: BossReactionResource,
  reactionCardId: CardRewardId | null = null,
  encounter: "boss" | "derby" = "boss",
): { win: number; loss: number } {
  const win = Math.round(bossReactionChance(state, resource, reactionCardId, encounter) * 100);
  return { win, loss: 100 - win };
}

export function rollBossReaction(winProbability: number, random: () => number = Math.random): boolean {
  return random() < clamp(winProbability, 0, 1);
}

export function cardProfileRatingBonus(cardId: CardRewardId | null, profileId: OpponentProfileId): number {
  if (!cardId) return 0;
  if (profileId === "aggressive" && cardId === "pase-entre-lineas") return 3;
  if (profileId === "compact" && (cardId === "remate-colocado" || cardId === "balon-parado")) return 3;
  if (profileId === "adaptive" && cardId === "presion-coordinada") return 3;
  return 0;
}

export function cardRatingBonus(cardId: CardRewardId | null, encounter: EncounterKind): number {
  if (!cardId) return 0;
  const reward = REWARDS[cardId];
  if (!reward.allowedEncounters.includes(encounter)) return 0;
  if (reward.effect.type === "rating") return reward.effect.amount;
  if (reward.effect.type === "bossRating" && encounter === "boss") return reward.effect.amount;
  return 0;
}

export function cardAssistChanceBonus(cardId: CardRewardId | null, encounter: EncounterKind): number {
  if (!cardId) return 0;
  const reward = REWARDS[cardId];
  if (!reward.allowedEncounters.includes(encounter) || reward.effect.type !== "assistChance") return 0;
  return reward.effect.percentagePoints / 100;
}

function availableRestPreparationKey(state: Pick<GameStateV2, "map" | "path" | "relicUses">): number | null {
  const nodes = new Map(state.map.flat().map((node) => [node.id, node.type]));
  for (let index = state.path.length - 1; index >= 0; index -= 1) {
    const nodeId = state.path[index]!;
    const type = nodes.get(nodeId);
    if (type === "rest") {
      const key = index + 1;
      return state.relicUses?.["plan-personalizado"] === key ? null : key;
    }
    if (type === "match" || type === "derby") return null;
  }
  return null;
}

export function restRecovery(
  state: Pick<GameStateV2, "inventory"> & Partial<Pick<GameStateV2, "physical">>,
  kind: "physical" | "morale" | "balanced",
): { physical: number; morale: number } {
  if (kind === "morale") return { physical: 0, morale: 24 };
  const relicPhysicalBonus = getRelicEffectAmount(state.inventory, "restPhysicalBonus");
  const physical = kind === "physical" ? 30 + relicPhysicalBonus : 12 + Math.floor(relicPhysicalBonus / 2);
  if (kind === "physical") return { physical, morale: 0 };
  const conversion = getRelicEffectAmount(state.inventory, "recoveryConversion");
  const effectivePhysicalRecovery = Math.min(physical, Math.max(0, 100 - (state.physical ?? 0)));
  return { physical, morale: 8 + Math.floor(effectivePhysicalRecovery * conversion / 100) };
}

export function userAssistOverrideChance(
  position: GameStateV2["player"]["position"],
  activeCardId: CardRewardId | null,
  encounter: EncounterKind,
): number {
  const baseline = position === "MCO" ? 0.48 : 0;
  return clamp(baseline + cardAssistChanceBonus(activeCardId, encounter), 0, 1);
}

export function buildPendingMatch(
  state: GameStateV2,
  encounterKind: EncounterKind,
  activeCardId: CardRewardId | null,
): PendingMatch {
  const activeEffect = activeCardId ? REWARDS[activeCardId].effect : null;
  const ratingModifier = cardRatingBonus(activeCardId, encounterKind);
  const restPreparationKey = availableRestPreparationKey(state);
  const postRestDiscount = restPreparationKey === null ? 0 : getRelicEffectAmount(state.inventory, "postRestCostReduction");
  const rawCost = ENCOUNTER_PHYSICAL_COST[encounterKind];
  const physicalCost = Math.max(0, rawCost - getRelicStackBonus(state.inventory, "botas-ligeras") - postRestDiscount);
  const physicalRecovery = activeEffect?.type === "physicalRestore" ? activeEffect.amount : 0;
  const moraleRecovery = activeEffect?.type === "moraleRestore" ? activeEffect.amount : 0;
  const ignorePhysicalPenalty = activeEffect?.type === "ignorePhysicalPenalty";
  let projectedPhysical = clamp(state.physical + physicalRecovery - physicalCost);
  const projectedWithoutCostReductions = clamp(state.physical + physicalRecovery - rawCost);
  const secondLung = REWARDS["segundo-pulmon"].effect;
  const canTriggerSecondLung = secondLung.type === "physicalSafety"
    && getInventoryCount(state.inventory, "segundo-pulmon") > 0
    && state.relicUses?.["segundo-pulmon"] !== state.season
    && projectedWithoutCostReductions < secondLung.threshold;
  if (canTriggerSecondLung) projectedPhysical = clamp(projectedPhysical + secondLung.amount);
  const projectedMorale = clamp(state.morale + moraleRecovery);
  const opponentProfile = getOpponentProfile(state, encounterKind);
  const matchupModifier = cardProfileRatingBonus(activeCardId, opponentProfile.id);
  let relicRatingBonus = 0;
  const biometric = REWARDS["pulsera-biometrica"].effect;
  if (biometric.type === "highPhysicalRating" && getInventoryCount(state.inventory, "pulsera-biometrica") > 0 && projectedPhysical >= biometric.threshold) relicRatingBonus += biometric.amount;
  const clipping = REWARDS["recorte-periodico"].effect;
  if (clipping.type === "streakRating" && getInventoryCount(state.inventory, "recorte-periodico") > 0) relicRatingBonus += Math.min(clipping.maximum, (state.winStreak ?? 0) * clipping.amount);
  const projectedCondition = { ...state, physical: projectedPhysical, morale: projectedMorale };
  const rating = effectiveRating(projectedCondition, ratingModifier + relicRatingBonus + matchupModifier, ignorePhysicalPenalty);
  const difficultyStep = encounterKind === "boss" ? state.boss?.round ?? 0 : state.week;
  return {
    encounterKind,
    activeCardId,
    ratingModifier,
    relicRatingBonus,
    assistChanceBonus: cardAssistChanceBonus(activeCardId, encounterKind),
    physicalCost,
    physicalRecovery,
    moraleRecovery,
    ignorePhysicalPenalty,
    secondLungTriggered: canTriggerSecondLung,
    restPreparationTriggered: postRestDiscount > 0,
    restPreparationKey: restPreparationKey ?? undefined,
    opponentProfileId: opponentProfile.id,
    matchupModifier,
    projectedPhysical,
    projectedMorale,
    effectiveRating: rating,
    winProbability: calculateEncounterWinProbability(
      rating,
      encounterDifficulty(encounterKind, state.season, difficultyStep),
    ),
  };
}
